import { TODO } from '@b2b/types/core';
import angular from 'angular';

function reservationInput() {
  return {
    restrict: 'A',
    controller: 'ReservationInputController',
    controllerAs: '$ctrl',
    templateUrl: 'common/directives/reservationInput/reservation-input.tpl.html',
    bindToController: {
      max: '<',
      min: '<',
      ignoreValue: '<',
      value: '<',
      addOn: '@',
      eventId: '@',
      label: '@',
      onBeforeTimeout: '&',
      onInputChange: '&',
      onReservationError: '&',
      onReservationSuccess: '&',
    },
  };
}

ReservationInputController.$inject = ['$timeout', 'ReservationService', 'UtilsService'];

/**
 * Controller responsible for managing reservation-input component
 * @class ReservationInputController
 * @constructor
 * @param {Function} $timeout - angular $timeout service
 * @param {Object} ReservationService - service to make reservation api calls
 * @param {Object} UtilsService - utilities function helpers
 * @return {ReservationInputController} - Class instance
 */
function ReservationInputController($timeout, ReservationService, UtilsService) {
  Object.assign(this, {
    $timeout: $timeout,
    ReservationService: ReservationService,
    UtilsService: UtilsService,
  });
}

/**
 * Angular lifecycle method $onInit
 * Ran when bindings are ready
 * @public
 * @memberOf ReservationInputController
 * @return {Undefined} -
 */
ReservationInputController.prototype.$onInit = function () {
  this.reservation = {};
  this.hasErrors = false;
  this.showInputAddOn = false;
  this.vm = this._getVm(this);
  this.timeout = {
    handler: null,
    response: this._timeoutResponse.bind(this),
    alertBeforeDefault: 60000,
  };
};

/**
 * @typedef $changesObj
 * @type {Object}
 * @property {*} currentValue - The actual value
 * @property {*} previousValue - The previous value
 * @property {Boolean} isFirstChange - True if this is the first change
 */

/**
 * Angular lifecycle method $onChanges
 * Ran when watched bindings change
 * @public
 * @memberOf ReservationInputController
 * @param {Object.<String, $changesObj>} changes - Angular changesObj
 * @return {Undefined} -
 */
ReservationInputController.prototype.$onChanges = function (changes) {
  var update = this.UtilsService.removeUndefinedProperties(changes, true),
    skipOnchange = this.vm && this.vm.value === update.value;

  Object.assign(this, update);
  this.vm = this._getVm(this);
  this.onChange(skipOnchange);
};

/**
 * Angular lifecycle method $onDestroy
 * Ran when component is about to be destroyed
 * @public
 * @memberOf ReservationInputController
 * @return {Undefined} -
 */
ReservationInputController.prototype.$onDestroy = function () {
  var reservationId = this.reservation.id;

  if (reservationId) {
    this.ReservationService.cancel(reservationId);
  }

  this.$timeout.cancel(this.timeout.handler);
};

/**
 * Input onChange function handler
 * Ran when the DOM INPUT changes
 * Updates the viewModel & and alert via callbacks anyone watching it
 * Invokes onInputChange
 * @memberOf ReservationInputController
 * @public
 * @return {Undefined} -
 */
ReservationInputController.prototype.onChange = function (skipOnChange) {
  var value = this._normalizeInput(this.vm.value),
    reservation = this.reservation || {},
    max = this.vm.max,
    min = this.vm.min,
    ignoreValue = this.vm.ignoreValue,
    clamped = 0,
    amount = 0;

  if (skipOnChange) {
    return;
  }

  clamped = this.UtilsService.clamp(value, min, max);
  amount = this.UtilsService.clamp(clamped - ignoreValue, 0, max);
  this.vm.value = clamped;
  this._callbackHandler(this.onInputChange, {
    $value: clamped,
  });

  if (amount > 0 || reservation.id) {
    // disable feature ENG-1199
    // this._reserveOrUpdateTicket(amount, reservation.id);
  }
};

/**
 * Manage the api call to reserve or update the ticket reservation
 * @memberOf ReservationInputController
 * @private
 * @param {Number} amount - quantity desired to reserve
 * @param {Number=} id - id the of the resevation
 * @return {Promise} - Promise request from the reservation service
 */
ReservationInputController.prototype._reserveOrUpdateTicket = function (amount, id) {
  const eventId = this.eventId;
  const handler = this._ticketResponseHandler.bind(this);

  if (!id) {
    const reservationItems = [
      {
        quantity: amount,
        eventId: eventId,
      },
    ];

    return this.ReservationService.create(reservationItems).then(handler, handler);
  }

  const reservationItems = this.reservation.reservationItems.map(function (reservationItem) {
    reservationItem.quantity = amount;

    return reservationItem;
  });

  return this.ReservationService.update(Object.assign(this.reservation, { reservationItems: reservationItems })).then(
    handler,
    handler
  );
};

/**
 * Helper function to ensure that input is always a number
 * @memberOf ReservationInputController
 * @private
 * @param {*} value - The value to be parsed
 * @return {Number} - The number representation of the input or 0 if it fails
 */
ReservationInputController.prototype._normalizeInput = function (value) {
  return parseInt(value, 10) || 0;
};

/**
 * Safely handles function invocations
 * Passing all the arguments received to the callback
 * @memberOf ReservationInputController
 * @private
 * @param {Function|String} callback - String -> tries to get it from the instance
 *                                     Function -> executes it
 * @param {*} Args - Arguments to be passed the callback
 * @example
 * _callbackHandler(this.onInputChange, 1, 2, 3);
 * // this.onInputChange will be executed as
 * // it is  good practive to bind the callback to avoid losing its context.
 * onInputChange(1,2,3);
 * @return {Undefined} -
 */
ReservationInputController.prototype._callbackHandler = function (callback) {
  var _callback = this[callback] || callback;
  var args = Array.prototype.slice.call(arguments, 1);

  if (_callback && typeof _callback === 'function') {
    // $timout service is used to refresh scopes
    // Avoiding a manual digest
    this.$timeout(function () {
      _callback.apply(null, args);
    }, 0);
  }
};

/**
 * @typedef ReservationInputController.viewModel
 * @type {Object}
 * @property {Number} inputValue - value of the input which will be rendered to the user
 *                                 defaults to 0
 * @property {String} inputAddOn - string containing add-on that will be rendered alonside the input
 *                                 defaults to null
 * @property {String} label - value of the label and aria-label of the input
 *                            defaults to empty string
 * @property {Number} max - max value to be considered as a valid input
 *                          defaults to 0
 * @property {Number} min - min value to be considere as a valid input
 *                          defaults to 0
 * @property {Number} ignoreValue - value to exclude from reservation calculations
 *                                 defaults to 0
 */

/**
 * Computes the viewModel and return it
 * @memberOf ReservationInputController
 * @private
 * @param {Object=} initialData - initial data used as a basis for the model
 *                                Returns default values if no data is passed
 * @return {ReservationInputController.viewModel} - The viewModel
 */
ReservationInputController.prototype._getVm = function (initialData) {
  var _data = initialData || {},
    inputValue = this._normalizeInput(_data.value),
    inputAddOn = _data.addOn || null,
    label = _data.label || '',
    max = this._normalizeInput(_data.max) || 99999,
    min = this._normalizeInput(_data.min) || 0,
    ignoreValue = this._normalizeInput(_data.ignoreValue) || 0;

  return {
    value: this.UtilsService.clamp(inputValue, min, max),
    inputAddOn: inputAddOn,
    label: label,
    max: Math.abs(max),
    min: Math.abs(min),
    ignoreValue: Math.abs(ignoreValue),
  };
};

/**
 * Helper function to compute the absolute difference between two dates in ms
 * @memberOf ReservationInputController
 * @private
 * @param {Date} dateA - Date input
 * @param {Date} dateB - Date input
 * @param {Number=} addToDiff - number to be added or subtracted of the final result
 * @return {Number} - Diff result
 */
ReservationInputController.prototype._getTimeDiff = function (dateA, dateB, addToDiff) {
  var diff = 0,
    extraAmount = addToDiff || 0;

  diff = Math.abs(dateA - dateB) + extraAmount;
  return diff;
};

/**
 * The callback that will be used to know if the user wants to renew his reservations before timing out
 * @memberOf ReservationInputController
 * @private
 * @param {Boolean} value - Any truthy value will be considered as valid input
 *                        - If true reservations will be renewed
 *                        - If false reservations won't be renewed
 * @return {Undefined} -
 */
ReservationInputController.prototype._timeoutResponse = function (value) {
  if (value) {
    this.onChange();
  }
};

/**
 * Cancels and computes the timeout that will be invoked X ms time before the ticket expires
 * It depends on the alertBeforeDefault config
 * @memberOf ReservationInputController
 * @private
 * @param {Date} now - Current date & time
 * @param {String} expirationString - ISO string date
 * @return {Undefined} -
 */
ReservationInputController.prototype._handleTimeout = function (now, expirationString) {
  var expirationDate = new Date(expirationString),
    timeout = 0,
    alertBefore = this.timeout.alertBeforeDefault,
    callback = this.onBeforeTimeout;

  if (isNaN(expirationDate.valueOf())) {
    return;
  }

  timeout = this._getTimeDiff(now, expirationDate, -alertBefore);
  this.$timeout.cancel(this.timeout.handler);
  this._setTimeout(timeout > 0 ? timeout : 0, callback);
};

/**
 * Effectively sets the timeout using angular $timeout service
 * @memberOf ReservationInputController
 * @private
 * @param {Number} timeout - amount in ms before invoking the callback
 * @param {Function} callback - Binding received in onBeforeTimeout
 * @return {Undefined} -
 */
ReservationInputController.prototype._setTimeout = function (timeout, callback) {
  var hasTimedOut = !timeout;
  let result: TODO | null = null;

  const handler = function () {
    this._callbackHandler(callback, result);
  };

  result = {
    callback: this.timeout.response,
    reservationId: this.reservation.id,
    hasTimedOut: hasTimedOut,
    timeoutIn: timeout,
  };

  this.timeout.handler = this.$timeout(handler.bind(this), timeout);
};

/**
 * Handles the reponse received from the reservation service
 * Invokes onReservationSuccess or onReservationError based on the response
 * @memberOf ReservationInputController
 * @private
 * @param {Object} response - Reservation api response
 * @return {Undefined} -
 */
ReservationInputController.prototype._ticketResponseHandler = function (response) {
  var _response = response || {},
    now = new Date(),
    success = !!_response.success,
    payload = _response.payload,
    error = _response.error,
    reservation = this.reservation;

  if (success) {
    this.reservation = Object.assign(reservation, payload);
    this._handleTimeout(now, reservation.expirationTimestamp);
    this._callbackHandler(this.onReservationSuccess, {
      $reservationId: reservation.id,
    });
  } else {
    this._callbackHandler(this.onReservationError, {
      $error: error,
    });
  }

  this.hasErrors = !success;
  this.showInputAddOn = this.vm.inputAddOn && this.hasErrors;
};

export const ReservationInputDirectiveModule = angular
  .module('acme.common.directive.reservationInput', ['acme.common.service.utils'])
  .directive('reservationInput', reservationInput)
  .controller('ReservationInputController', ReservationInputController).name;
